import { useContext, useEffect, useRef, useState } from "react";
import './index.less'
import GlobalSidebar from "./sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, InputRef, Popconfirm, Popover, Space, Table } from "antd";
import {
    LogoutOutlined,
    UploadOutlined,
    EllipsisOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import dayjs from "dayjs";
import classnames from "classnames";
import { Project } from "@/declare/interface/project/project";
import { createProject, deleteProject, findProjectDetail, findProjects, findProjectsByUser, updateProject } from "@/declare/api/project/project";
import localStore from "@/store/local-store";
import { AxesPrimitive } from "pytha2";
import { ContextMenu } from "bimui";
import { ProjectGroup } from "@/declare/interface/project/project-group";
import { findProjectGroupDetail } from "@/declare/api/project/project-group";
import { BaseUtils } from "tnbase";
import { NavigationContext } from "@/App";

/**
 * `GlobalLayout` 组件
 * 
 * - 提供项目列表和图纸管理功能的主界面。
 * - 支持新建、删除、重命名、复制项目功能，以及按名称搜索图纸。
 * - 包括：项目的侧边栏导航和图纸管理的主内容区域。
 */

export default function GlobalLayout() {
    /**
     * 当前项目组uuid，从路由参数中获取
     */
    const { projectGroupUuid } = useParams();
    /**
     * 加载状态，用于显示加载效果
     */
    const [loading, setLoading] = useState<boolean>(false);

    /**
     * 右键菜单是否可见
     */
    const [menuVisible, setMenuVisible] = useState<boolean>(false);

    /**
     * 当前通过表格中每行的更多按钮选中的图纸对象
     */
    const [selected, setSelected] = useState<Project>();

    /**
     * 所有的图纸文件
     */
    const [files, setFiles] = useState<Project[]>([]);

    /**
     * 显示在页面上的图纸文件（经过搜索过滤）
     */
    const [displayFiles, setDisplayFiles] = useState<Project[]>([]);

    /**
     * 搜索框的输入值
     */
    const [searchValue, setSearchValue] = useState<string>('');

    /**
     * 当前正在编辑的文件名
     */
    const [filename, setFilename] = useState<string>('');

    /**
     * - 在组件内控制页面跳转。
     */
    const navigate = useNavigate();

    /**
     * 当前项目对象，包含项目的详细信息
     */
    const [projectGroup, setProjectGroup] = useState<ProjectGroup>();

    /**
     * - 用于绑定到表单输入框的引用，以便控制其聚焦等操作。
     */
    const ref = useRef<InputRef>(null)

    /**
     * 是否显示右键菜单
     */
    const [cmVisible, setCmVisible] = useState<boolean>(false);

    /**
     * 右键菜单的水平位置
     */
    const [contextMenuLeft, setContextMenuLeft] = useState<number>();

    /**
     * 右键菜单的垂直位置
     */
    const [contextMenuTop, setContextMenuTop] = useState<number>();

    /**
     * 当前右键选中的项目
     */
    const [selectProject, setSelectProject] = useState<Project>();
    /**
     * 导航上下文
     */
    const navigationContext = useContext(NavigationContext);

    /**
     * 当 `projectId` 发生变化时触发根据当前项目 ID 初始化页面数据
     * 
     * 检查 `projectId` 是否为有效数字。
     * 如果无效，则清空文件列表和显示内容。
     * 如果有效，调用 `generateData` 方法加载项目数据。
     */
    useEffect(() => {
        if (!projectGroupUuid) {
            setFiles([]);
            setDisplayFiles([]);
            return;
        }
        generateData(projectGroupUuid);
    }, [projectGroupUuid])
    /**
     * 初始化项目和图纸数据
     * - 根据 `projectGroupUuid` 从后端获取项目详情和图纸数据。
     * - 数据加载后，按照最后修改时间降序排列图纸列表。
     * 
     * @param projectGroupUuid 项目组uuid
     */
    const generateData = (projectGroupUuid) => {
        setLoading(true)
        findProjectGroupDetail(projectGroupUuid).then(res => {
            setProjectGroup(res.data)
            return findProjectsByUser(projectGroupUuid, localStore.userUuid);
        })
            .then(res => {
                let files: Project[] = res.data || [];
                // TODO
                // files = files.sort((a, b) => b.lastModifiedTime - a.lastModifiedTime)
                setFiles(files);
                handleSearchChange(searchValue, files);
            }).finally(() => {
                setLoading(false)
            })
    }
    /**
     * 根据搜索值过滤图纸列表
     * 
     * @param value 搜索框输入的值
     * @param files 全部图纸列表
     */
    const handleSearchChange = (value, files: Project[]) => {
        if (!value) {
            setDisplayFiles(files);
            return;
        }
        let displayFiles = files.filter(item => item.name.match(value)) || [];
        setDisplayFiles(displayFiles);
    }
    /**
     * 创建新图纸
     * - 生成新图纸的 UUID 并保存到后端。
     * - 跳转到新创建图纸的页面。
     */
    const create = () => {
        if (!projectGroupUuid) return;
        const axes = new AxesPrimitive(null, true);
        let uuid = BaseUtils.genUUID();
        let newFile = {
            uuid: uuid,
            name: `新项目${uuid.slice(0, 6)}`,
            lastModifiedTime: new Date(),
            projectGroupUuid: projectGroupUuid,
            axes: axes.toJson(),
            demo: !!projectGroup?.demo
        };
        setLoading(true);
        createProject(newFile).then(res => {
            onAllScreen(); // 激活全屏状态
            navigate(`/projects/${uuid}`)
        }).finally(() => {
            setLoading(false);
        })
    }

    /**
     * 修改图纸名称
     * - 修改图纸对象的 `name` 属性并保存到后端。
     * - 更新页面上的图纸列表。
     * 
     * @param record 要修改的图纸对象
     * @param value 新的文件名
     */
    const changeName = (record: Project, value: string) => {
        record.renaming = false;
        record.name = value;
        updateProject(record.uuid, record).then(res => {
            setDisplayFiles([...displayFiles]);
        })
    }
    /**
     * 点击图纸文件进行跳转
     * - 保存图纸名称到本地存储。
     * - 更新最后修改时间并跳转到对应图纸页面。
     * 
     * @param record 当前点击的图纸对象
     */
    const onGraphicClick = (record) => {
        localStore.projectName = record.name;
        let data = {
            ...record,
            lastModifiedTime: new Date(),
        }
        updateProject(record.uuid, data).then(res => { })
        onAllScreen(); // 激活全屏状态
        navigate(`/projects/${record.uuid}`);

    }
    /**
     * 删除图纸
     * @param record 要删除的图纸对象
     */
    const onDelete = (record: Project) => {
        setMenuVisible(false);
        setLoading(true);
        deleteProject(record.uuid).then(res => {
            let filterFiles = files.filter(item => item.uuid != record.uuid)
            setFiles(filterFiles);
            handleSearchChange(searchValue, filterFiles);
        }).finally(() => {
            setLoading(false);
        })
    }
    /**
     * 导入本地文件，触发文件选择框
     */
    const onFileImport = () => {
        if (!projectGroupUuid) return;
        let input = document.getElementById('dxf-global');
        input.click();
    }
    /**
     * 文件选择回调事件 // TODO
     * @param event 文件选择事件
     */
    // const onFileSelected = (event) => {
    //     let reader = new FileReader();
    //     let file = event.target.files[0];
    //     if (file == null) return;
    //     setFilename(file.name)
    //     reader.onloadend = fileLoad;
    //     reader.readAsText(file);
    // }

    /**
     * 读取文件内容 TODO
     * @param event 文件加载事件
     */
    // const fileLoad = (event) => {
    //     let uuid = genUUID();
    //     let newFile = {
    //         uuid: uuid,
    //         name: filename,
    //         projectGroupUuid: projectGroupUuid
    //     };
    //     saveGraphicProject(newFile).then(res => {
    //         let fileReader = event.target;
    //         if (fileReader.error) return console.log("error onloadend!");
    //         onAllScreen(); // 激活全屏状态
    //         navigate(`/project/${uuid}`)
    //     })
    // }


    /**
     * 退出登录
     */
    const logOut = () => {
        localStore.clear();
        navigate('/login');
    }

    /**
     * 右键菜单渲染
     * 删除、重命名、复制。
     */
    const menu = (
        <div className="menu">
            <div className="menu-item" onClick={(e) => {
                onDelete(selected)
            }}>删除</div>
            <div className="menu-item" onClick={(e) => {
                selected.renaming = true;
                setMenuVisible(false);
                setTimeout(() => {
                    ref.current?.focus({
                        cursor: 'all',
                    })
                }, 1)
            }}>重命名</div>
            {/* TODO */}
            {/* <div className="menu-item" onClick={(e) => {
                onCopy(selected)
            }}>复制项目</div> */}
        </div>
    )
    /**
     * 复制选中的项目
     * @param project 要复制的项目
     */
    // const onCopy = (project: graphicProject) => {
    //     if (isNaN(+projectId)) return;
    //     let uuid = genUUID();
    //     let newFile = {
    //         uuid: uuid,
    //         name: `新项目${uuid.slice(0, 6)}`,
    //         lastModifiedTime: new Date(),
    //         projectId: +projectId
    //     };
    //     setLoading(true);
    //     copyGraphicProject(newFile, { oldGraphictUuid: project.uuid }).then(res => {
    //         onAllScreen(); // 激活全屏状态
    //         navigate(`/project/${uuid}`)
    //     }).finally(() => {
    //         setLoading(false);
    //     })
    // }

    /**
     * 激活全屏状态
     */
    const onAllScreen = () => {
        let elem = document.documentElement;
        if (!document.fullscreenElement && elem?.requestFullscreen) {
            elem?.requestFullscreen();
        }
    }

    const onClickSystemLib = () => {
        navigate("/system-lib");
    }

    return (
        <div className="global-content">
            <div className="global-content-view">
                <GlobalSidebar />
                <div className="global-page-route">
                    <div className="top-tools">
                        <Space>
                            <Button onClick={onClickSystemLib} loading={loading} >
                                切换至系统库
                            </Button>
                            <Button danger onClick={create} loading={loading} disabled={!projectGroupUuid}>
                                新建项目
                            </Button>
                            <div className="file-import-global">
                                {/* TODO */}
                                {/* <Button block className="file-btn" onClick={onFileImport} disabled={!projectGroupUuid}><UploadOutlined />导入本地文件</Button>
                                <input type="file" accept=".dxf" id="dxf-global" onChange={onFileSelected} style={{ display: 'none' }} /> */}
                            </div>
                            <Input
                                placeholder="搜索"
                                prefix={<SearchOutlined />}
                                className="search-input"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                onPressEnter={() => handleSearchChange(searchValue, files)}
                                onBlur={() => handleSearchChange(searchValue, files)} />
                            <div className="right">
                                <div className="action logout-btn" style={{ "position": "absolute", "right": "5px" }} onClick={logOut}>
                                    <LogoutOutlined />
                                    <span className="content">退出登录</span>
                                </div>
                            </div>
                        </Space>

                    </div>
                    <div className="main-content">
                        <Table
                            rowKey="uuid"
                            dataSource={displayFiles}
                            pagination={false}
                            loading={loading}
                            rowClassName="row"
                            onRow={(record) => {
                                return {
                                    onContextMenu: e => {
                                        e.preventDefault();
                                        let clientX = e.clientX;
                                        let clientY = e.clientY;
                                        setContextMenuLeft(clientX);
                                        setContextMenuTop(clientY);
                                        setSelectProject(record);
                                        setCmVisible(true);
                                    }
                                }
                            }}>
                            <Table.Column title="名称" ellipsis render={record => {
                                return (
                                    <div className="file-item">
                                        <div className="name">
                                            {(!!record.renaming) ? (
                                                <Input
                                                    className="input"
                                                    ref={(record.uuid === selected.uuid || record.uuid === selectProject.uuid) ? ref : null}
                                                    size="small"
                                                    bordered={false}
                                                    style={{ backgroundColor: '#fff' }}
                                                    defaultValue={record.name}
                                                    onBlur={(e) => changeName(record, e.target.value)}
                                                    //@ts-ignore
                                                    onPressEnter={(e) => changeName(record, e.target.value)} />
                                            ) : (
                                                <div style={{ cursor: 'pointer' }} onClick={e => onGraphicClick(record)}>
                                                    {record.name}
                                                </div>
                                            )}
                                        </div>
                                        <div className="fill-remaining-space"></div>
                                        <div className={classnames({
                                            "extra-action": true,
                                        })}>
                                            <Popover
                                                trigger="click"
                                                placement="bottomLeft" content={menu}
                                                open={menuVisible && record.uuid === selected.uuid}
                                                onOpenChange={(e) => {
                                                    setMenuVisible(e);
                                                }}
                                            >
                                                <div className="menu-btn" onClick={e => {
                                                    setSelected(record)
                                                }}>
                                                    <EllipsisOutlined />
                                                </div>
                                            </Popover>
                                        </div>
                                    </div>
                                )
                            }} />
                            <Table.Column title="创建人" width='25%' render={record => (
                                <div>
                                    {record.createUserName}
                                </div>
                            )} />
                            {/* <Table.Column title="最近查看" width='20%'
                                sorter={(a: Project, b: Project) => {
                                    return a.lastModifiedTime > b.lastModifiedTime ? 1 : -1;
                                }}
                                render={record => (
                                    <div>
                                        {record.lastModifiedTime ? dayjs(record.lastModifiedTime).format('YYYY-MM-DD HH:mm') : null}
                                    </div>
                                )} /> */}
                        </Table>
                    </div>
                </div>
            </div>
            <ContextMenu
                visible={cmVisible}
                left={contextMenuLeft}
                top={contextMenuTop}
                width={140}
                onCancel={() => {
                    setCmVisible(false);
                    setSelectProject(null);
                }}
                wrapperClassname='cm-container'
            >
                <div className='cm-body'>
                    <div className={classnames({
                        'option': true,
                        'disabled': false
                    })} onClick={() => {
                        setCmVisible(false);
                        selectProject.renaming = true; // 设置右键选中的项目为重命名状态
                        setSelected(selectProject); // 同时更新 `selected`，以便复用 "更多按钮" 的逻辑
                        setTimeout(() => {
                            ref.current?.focus({
                                cursor: 'all',
                            })
                        }, 1)
                    }}>重命名</div>
                    <Popconfirm
                        title="确认删除"
                        onCancel={() => {
                            setSelectProject(null);
                            setCmVisible(false);
                        }}
                        onConfirm={() => onDelete(selectProject)}>
                        <div className={classnames({
                            'option': true,
                            'disabled': false
                        })}>删除项目</div>
                    </Popconfirm>
                </div>
                {/* <div className='cm-body'>
                    <Popconfirm
                        title="确认复制"
                        onCancel={() => {
                            setSelectProject(null);
                            setCmVisible(false);
                        }}
                        onConfirm={() => onCopy(selectProject)}>
                        <div className={classnames({
                            'option': true,
                            'disabled': false
                        })}>复制项目</div>
                    </Popconfirm>
                </div> */}

            </ContextMenu>
        </div>
    )
}